import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { DataTable } from '@scuf/datatable';
import { downloadFileExcelReport } from 'resources/api/bslbr_api/fileReports';
import { notificationAtom } from 'resources/atoms/notificationAtom';
import { FilterReportDTO, ReportDTO } from 'models/bslbr/general';

interface TableReportProps {
    reportType: string | null;
    reportItems: number;
    reportData: ReportDTO | null;
    reportFilter: FilterReportDTO;
    reportLoading: boolean;
    onPageChange: (pageNumber: number) => Promise<void>;
}

const TableReport: React.FC<TableReportProps> = ({
    reportType,
    reportData,
    reportFilter,
    reportItems,
    reportLoading,
    onPageChange
}) => {
    const { t } = useTranslation(['common', 'bslbr', 'user', 'home', 'notification']);
    const setNotification = useSetRecoilState(notificationAtom);
    const [filteredData, setFilteredData] = useState<any>([]);
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false);

    useEffect(() => {
        setFilteredData(reportData);
    }, [reportData]);

    if (!reportType) {
        return null;
    }

    if (!reportData) {
        return null;
    }

    const handleSuccess = () => {
        setNotification({ type: 'success', message: 'Report' });
    };

    const handleError = () => {
        setNotification({ type: 'information', message: 'NoData' });
    };

    const handleExportExcel = async () => {
        setLoadingDownload(true);
        await downloadFileExcelReport(reportFilter, `report.xlsx`, handleSuccess, handleError).finally(() =>
            setLoadingDownload(false)
        );
    };

    const renderTable = () => {
        switch (reportType) {
            case 'Contracts':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="TypeService"
                                align="center"
                                header={t('bslbr:translation.TypeServices')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Company"
                                align="center"
                                header={t('bslbr:translation.Company')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Contract"
                                align="center"
                                header={t('common:translation.Contract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="StatusContract"
                                align="center"
                                header={t('bslbr:translation.StatusContract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Warranty"
                                align="center"
                                header={t('bslbr:translation.Warranty')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="SaleDate"
                                align="center"
                                header={t('bslbr:translation.SaleDate')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column
                                field="AddOn"
                                align="center"
                                header={t('bslbr:translation.AddOn')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Equipments':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Contract"
                                align="center"
                                header={t('common:translation.Contract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="SerialNumber"
                                align="center"
                                header={t('common:translation.SerialNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="AddOn"
                                align="center"
                                header={t('bslbr:translation.AddOn')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Deliveries':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="DeliveryNumber"
                                align="center"
                                header={t('bslbr:translation.IdDelivery')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Company"
                                align="center"
                                header={t('bslbr:translation.Company')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartnerReceived"
                                align="center"
                                header={t('bslbr:translation.PartnerReceived')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column
                                field="BillofLading"
                                align="center"
                                header={t('bslbr:translation.BillofLading')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Carrier"
                                align="center"
                                header={t('bslbr:translation.Carrier')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'ItemsInventories':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Parts':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumberDescription"
                                align="center"
                                header={t('bslbr:translation.PartDescription')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Product"
                                align="center"
                                header={t('common:translation.Products')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'ProductParts':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Product"
                                align="center"
                                header={t('common:translation.Product')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="ProductDescription"
                                align="center"
                                header={t('bslbr:translation.ProductDescription')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'PendingItemsInventories':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Products':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="Product"
                                align="center"
                                header={t('common:translation.Product')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="ProductDescription"
                                align="center"
                                header={t('bslbr:translation.ProductDescription')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Quotation':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column field="User" align="center" header={t('bslbr:translation.User')} sortable={true} />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'RepairParts':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column field="IdRepair" align="center" header={t('ID')} sortable={true} />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('bslbr:translation.Company')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Contract"
                                align="center"
                                header={t('bslbr:translation.Contract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="SerialNumber"
                                align="center"
                                header={t('common:translation.SerialNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column
                                field="Technical"
                                align="center"
                                header={t('bslbr:translation.Technical')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="IdInventory"
                                align="center"
                                header={t('bslbr:translation.IdItemInventory')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="IdItemPending"
                                align="center"
                                header={t('bslbr:translation.IdPendingItemInventory')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Pending"
                                align="center"
                                header={t('bslbr:translation.Pending')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Repair':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="IdRepair"
                                align="center"
                                header={t('ID')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="StatusRepair"
                                align="center"
                                header={t('bslbr:translation.StatusRepair')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="TypeService"
                                align="center"
                                header={t('bslbr:translation.TypeServices')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="User"
                                align="center"
                                header={t('bslbr:translation.User')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Technical"
                                align="center"
                                header={t('bslbr:translation.Technical')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="SerialNumber"
                                align="center"
                                header={t('common:translation.SerialNumber')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column field="SLA" align="center" header={t('SLA')} sortable={true} initialWidth={150} />
                            <DataTable.Column
                                field="Quantity"
                                align="center"
                                header={t('common:translation.Quantity')}
                                sortable={true}
                                initialWidth={150}
                            />
                            <DataTable.Column
                                field="FinalPrice"
                                align="center"
                                header={t('bslbr:translation.FinalPrice')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="PartNumberUsed"
                                align="center"
                                header={t('PartNumberUsed')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DescriptionPartNumber"
                                align="center"
                                header={t('common:translation.PartNumberUsed')}
                                sortable={true}
                                initialWidth={350}
                            />
                            <DataTable.Column
                                field="EndDate"
                                align="center"
                                header={t('bslbr:translation.FinishedDate')}
                                sortable={true}
                                initialWidth={200}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'IdStatus':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="IdRepair"
                                align="center"
                                header={t('ID')}
                                sortable={true}
                                initialWidth={200}
                            />

                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                                initialWidth={200}
                            />

                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="SerialNumber"
                                align="center"
                                header={t('common:translation.SerialNumber')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DateReceivedForRepair"
                                align="center"
                                header={t('bslbr:translation.EnteredRepair')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DateDiagnosis"
                                align="center"
                                header={t('bslbr:translation.Diagnosis')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DateRepairing"
                                align="center"
                                header={t('bslbr:translation.Repairing')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DateWaitingForParts"
                                align="center"
                                header={t('bslbr:translation.WaitingParts')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="DateShippedToCustomer"
                                align="center"
                                header={t('bslbr:translation.SentCustomer')}
                                sortable={true}
                                initialWidth={200}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'InventoryTotalPrice':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="IdInventory"
                                align="center"
                                header={t('ID')}
                                sortable={true}
                                initialWidth={200}
                            />

                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Product"
                                align="center"
                                header={t('common:translation.Product')}
                                sortable={true}
                                initialWidth={200}
                            />

                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="PartNumberDescription"
                                align="center"
                                header={t('bslbr:translation.PartDescription')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="Quantity"
                                align="center"
                                header={t('bslbr:translation.Quantity')}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="FinalPrice"
                                align="center"
                                header={t('bslbr:translation.FinalPrice') + ' ($ USD)'}
                                sortable={true}
                                initialWidth={200}
                            />
                            <DataTable.Column
                                field="TotalPrice"
                                align="center"
                                header={t('bslbr:translation.TotalPrice') + ' ($ USD)'}
                                sortable={true}
                                initialWidth={200}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'BounceRate':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column field="IdRepair" align="center" header={t('ID')} sortable={true} />
                            <DataTable.Column
                                field="SerialNumber"
                                align="center"
                                header={t('common:translation.SerialNumber')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Company"
                                align="center"
                                header={t('bslbr:translation.Company')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column
                                field="QtdBounceRate"
                                align="center"
                                header={t('bslbr:translation.BounceRateQtd')}
                                sortable={true}
                            />{' '}
                            <DataTable.Column
                                field="BounceRateDays"
                                align="center"
                                header={t('bslbr:translation.BounceRateDays')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'OTTR':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="StatusRepair"
                                align="center"
                                header={t('bslbr:translation.StatusRepair')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="TypeService"
                                align="center"
                                header={t('bslbr:translation.TypeServices')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('bslbr:translation.Company')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Contract"
                                align="center"
                                header={t('bslbr:translation.Contract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column field="SLA" align="center" header="OTTR" sortable={true} />
                            <DataTable.Column
                                field="SLADays"
                                align="center"
                                header={t('bslbr:translation.SLADays')}
                                sortable={true}
                            />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'MCPR':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>

                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />

                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                            <DataTable.Column
                                field="CountRepair"
                                align="center"
                                header={t('bslbr:translation.CountRepair')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="MaterialCost"
                                align="center"
                                header={t('bslbr:translation.MaterialCost') + ' ($ USD)'}
                                sortable={true}
                            />
                            <DataTable.Column field="MCPR" align="center" header="MCPR ($ USD)" sortable={true} />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            case 'Inventory':
                return (
                    <>
                        <DataTable
                            columnResizeMode="fit"
                            data={filteredData ?? []}
                            resizableColumns={true}
                            loading={reportLoading}
                        >
                            <DataTable.HeaderBar>
                                <DataTable.HeaderBar.Item
                                    content={t('bslbr:translation.ExportExcel')}
                                    iconSize="small"
                                    icon="file-download"
                                    onClick={handleExportExcel}
                                    loading={loadingDownload}
                                />
                            </DataTable.HeaderBar>
                            <DataTable.Column
                                field="StatusRepair"
                                align="center"
                                header={t('bslbr:translation.StatusRepair')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="TypeService"
                                align="center"
                                header={t('bslbr:translation.TypeServices')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Partner"
                                align="center"
                                header={t('common:translation.Partner')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Customer"
                                align="center"
                                header={t('bslbr:translation.Customer')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="Contract"
                                align="center"
                                header={t('bslbr:translation.Contract')}
                                sortable={true}
                            />
                            <DataTable.Column
                                field="PartNumber"
                                align="center"
                                header={t('common:translation.PartNumber')}
                                sortable={true}
                            />
                            <DataTable.Column field="Country" align="center" header={t('user:address.Country')} sortable={true} />
                        </DataTable>
                        <DataTable.Pagination
                            totalItems={reportItems}
                            itemsPerPage={10}
                            onPageChange={pageNumber => onPageChange(Number(pageNumber))}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="form4Columns">
                <div className="Table-report">{renderTable()}</div>
            </div>
        </>
    );
};

export default TableReport;
